import React, { useMemo, useState } from 'react'
import { SingleDatePicker, CalendarDay, SingleDatePickerShape } from 'react-dates'
import { useTranslation } from 'react-i18next'
import { SerializedStyles, css } from '@emotion/core'
import moment from 'moment'
import { DeleteIcon } from './settings/icon/delete-icon'

// model

type DateFieldProps = Partial<SingleDatePickerShape> & {
  value?: moment.Moment | string | null
  onChange: (value: moment.Moment | null) => void
  displayFormat?: string
  error?: string
  placeholder?: string
  customCss?: SerializedStyles
  isOutsideRange?: (day: moment.Moment) => boolean
  isDayBlocked?: (day: moment.Moment) => boolean
  enableOutsideDays?: boolean
  initialVisibleMonth?: (() => moment.Moment) | null
  appendToBody?: boolean
  openDirection?: 'up' | 'down'
  withPortal?: boolean
  id?: string
}

export const DateField: React.FC<DateFieldProps> = ({
  value,
  onChange,
  displayFormat,
  error,
  placeholder,
  customCss,
  showClearDate,
  id = 'date',
  isOutsideRange,
  isDayBlocked,
  enableOutsideDays,
  initialVisibleMonth,
  withPortal,
  appendToBody,
  openDirection,
  ...restProps
}) => {
  const { t } = useTranslation()
  const [calendarFocused, setCalendarFocused] = useState<boolean>(false)

  const dateFieldWrapperStyle = useMemo(
    () =>
      css({
        position: 'relative',
        'div.SingleDatePicker': {
          '.SingleDatePickerInput__withBorder': { width: showClearDate ? 200 : 182 },
          '.SingleDatePickerInput': {
            display: 'flex',
            alignItems: 'center',
            ...(appendToBody || withPortal
              ? {}
              : {
                  'div.SingleDatePicker_picker': {
                    zIndex: 99,
                    'div.DayPicker':
                      openDirection === 'up'
                        ? { left: 0, top: -230 }
                        : {
                            position: 'absolute',
                            left: -10,
                            top: 138,
                          },
                  },
                }),
            'div.DateInput': {
              'input.DateInput_input': { fontSize: 14, fontWeight: 'normal' },
            },
          },
        },
        '.CalendarDay__outside': { color: '#cccccc' },
      }),
    [openDirection, appendToBody, withPortal, showClearDate],
  )

  return (
    <div css={[dateFieldWrapperStyle, customCss]}>
      <SingleDatePicker
        date={typeof value === 'string' ? moment(value) : value || null}
        onDateChange={changeDate => onChange(changeDate)}
        focused={calendarFocused}
        onFocusChange={v => setCalendarFocused(Boolean(v.focused))}
        numberOfMonths={1}
        displayFormat={displayFormat || t('MM-DD-YYYY')}
        monthFormat={t('YYYY-MM')}
        placeholder={placeholder}
        id={id}
        hideKeyboardShortcutsPanel={true}
        customInputIcon={<img src={require('@/static/images/calendar_icon.svg')} width={14} />}
        navPrev={
          <div className="monthPrev">
            <img
              alt="arrow"
              src={require('@/static/images/icon_caalender_left.svg')}
              onMouseOver={e => (e.currentTarget.src = require('@/static/images/icon_caalender_left.svg'))}
              onMouseOut={e => (e.currentTarget.src = require('@/static/images/icon_caalender_left.svg'))}
            />
          </div>
        }
        navNext={
          <div className="monthNext">
            <img
              alt="arrow"
              src={require('@/static/images/icon_caalender_right.svg')}
              onMouseOver={e => (e.currentTarget.src = require('@/static/images/icon_caalender_right.svg'))}
              onMouseOut={e => (e.currentTarget.src = require('@/static/images/icon_caalender_right.svg'))}
            />
          </div>
        }
        renderCalendarDay={calendarDayProps => (
          <CalendarDay
            {...calendarDayProps}
            renderDayContents={day => (
              <div className="day">
                <span>{day?.format('D')}</span>
              </div>
            )}
          />
        )}
        {...restProps}
        withPortal={withPortal}
        initialVisibleMonth={initialVisibleMonth ?? undefined}
        enableOutsideDays={enableOutsideDays ?? true}
        isOutsideRange={isOutsideRange ?? (() => false)}
        isDayBlocked={isDayBlocked}
      />
      {showClearDate && value && <DeleteIcon onClick={() => onChange(null)} style={{ position: 'absolute', top: 1, left: 165 }} />}
      {!!error && <div css={{ fontSize: 10, color: 'rgba(255,0,0,0.5)', marginTop: 4 }}>{error}</div>}
    </div>
  )
}
